import React, { useState, useEffect } from "react";
import { FlashcardCollection, Flashcard } from "../types";
import {
  FaArrowLeft, FaUndo, FaCheck, FaTimes, FaLayerGroup,
  FaExternalLinkAlt, FaTag
} from "react-icons/fa";
import ReactMarkdown from "react-markdown";
import "../styles_1/FlashcardGenerator.css"; // Reuse generator styles

interface FlashcardViewerProps {
  collection: FlashcardCollection;
  onBack: () => void;
  onUpdateCollection?: (collection: FlashcardCollection) => void;
}

// --- PURE HELPER FUNCTIONS (Outside component for performance) ---
const sanitizeContent = (text: string) => {
  if (!text) return "";
  return text
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<strong>(.*?)<\/strong>/gi, "**$1**")
    .replace(/<p>(.*?)<\/p>/gi, "$1\n")
    .replace(/<b>(.*?)<\/b>/gi, "**$1**")
    .replace(/<i>(.*?)<\/i>/gi, "_$1_")
    .replace(/a recurring theme in UPSC Prelims and Mains questions\./gi, "")
    .replace(/recurring theme in UPSC\./gi, "")
    .replace(/Important for UPSC Prelims and Mains\./gi, "")
    .replace(/\. ([A-Z])/g, ".\n$1")
    .replace(/(\*\*Step-by-Step Logic:\*\*)/gi, "\n$1\n")
    .replace(/(\*\*Explanation:\*\*)/gi, "\n$1\n")
    .replace(/(\*\*Context:\*\*)/gi, "$1\n")
    .replace(/(\*\*Core Detail:\*\*)/gi, "\n$1\n")
    .replace(/(\*\*Answer:\*\*)/gi, "$1\n")
    .trim();
};

export const FlashcardViewer: React.FC<FlashcardViewerProps> = ({ collection, onBack, onUpdateCollection }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [sessionStats, setSessionStats] = useState({ correct: 0, wrong: 0 });
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [showResult, setShowResult] = useState(false); // For MCQ feedback
  const [jumpTo, setJumpTo] = useState("");

  if (!collection.flashcards || collection.flashcards.length === 0) {
    return (
      <div className="generator" style={{ textAlign: 'center', padding: '100px' }}>
        <h2>No cards in this deck</h2>
        <button onClick={onBack} className="secondary-btn">Go Back</button>
      </div>
    );
  }

  const currentCard = collection.flashcards[currentIndex];
  if (!currentCard) return null;

  const isMCQ = currentCard.type === "mcq";

  // Reset state when card changes
  useEffect(() => {
    setIsFlipped(false);
    setSelectedOption(null);
    setShowResult(false);
  }, [currentIndex]);


  const handleNext = () => {
    if (currentIndex < collection.flashcards.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      const percentage = (sessionStats.correct / collection.flashcards.length) * 100;
      alert(`Session Complete!\nScore: ${sessionStats.correct}/${collection.flashcards.length} (${percentage.toFixed(0)}%)`);

      if (onUpdateCollection) {
        onUpdateCollection({
          ...collection,
          lastAttempt: {
            timestamp: new Date().toISOString(),
            score: sessionStats.correct,
            total: collection.flashcards.length,
            percentage
          }
        });
      }
      onBack();
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) setCurrentIndex(prev => prev - 1);
  };

  const handleRate = (correct: boolean) => {
    setSessionStats(prev => ({
      correct: prev.correct + (correct ? 1 : 0),
      wrong: prev.wrong + (correct ? 0 : 1)
    }));
    handleNext();
  };

  const handleJump = () => {
    const val = parseInt(jumpTo);
    if (!isNaN(val) && val > 0 && val <= collection.flashcards.length) {
      setCurrentIndex(val - 1);
      setJumpTo("");
    }
  };

  // --- MCQ LOGIC ---
  const handleOptionClick = (option: string, index: number) => {
    if (showResult) return;
    setSelectedOption(option);
    setShowResult(true);

    const selectedLetter = String.fromCharCode(65 + index);
    const isCorrect = selectedLetter === (currentCard.correctAnswer?.trim().toUpperCase());

    setSessionStats(prev => ({
      correct: prev.correct + (isCorrect ? 1 : 0),
      wrong: prev.wrong + (isCorrect ? 0 : 1)
    }));
  };

  // Progress Bar Calculation
  const progress = ((currentIndex + 1) / collection.flashcards.length) * 100;

  return (
    <div className="generator" style={{ maxWidth: '900px', margin: '0 auto', minHeight: '80vh' }}>

      {/* HEADER */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <button onClick={onBack} className="secondary-btn" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <FaArrowLeft /> Back
        </button>
        <div style={{ textAlign: 'right' }}>
          <h2 style={{ margin: 0, color: '#1e293b' }}>{collection.name}</h2>
          <div style={{ fontSize: '0.9rem', color: '#64748b' }}>
            Card {currentIndex + 1} of {collection.flashcards.length}
          </div>
        </div>
      </div>

      {/* PROGRESS BAR */}
      <div style={{ display: 'flex', gap: '15px', alignItems: 'center', marginBottom: '30px' }}>
        <div style={{ flex: 1, height: '8px', background: '#e2e8f0', borderRadius: '10px', overflow: 'hidden' }}>
          <div style={{ width: `${progress}%`, height: '100%', background: '#8b5cf6', transition: 'width 0.3s ease' }} />
        </div>
        <div className="jump-box" style={{ display: 'flex', gap: '5px' }}>
          <input
            type="number"
            placeholder="Jump to..."
            value={jumpTo}
            onChange={(e) => setJumpTo(e.target.value)}
            style={{ width: '60px', padding: '4px 8px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.8rem' }}
            onKeyDown={(e) => e.key === 'Enter' && handleJump()}
          />
          <button onClick={handleJump} className="secondary-btn" style={{ padding: '4px 8px', fontSize: '0.8rem' }}>Go</button>
        </div>
      </div>

      {/* CARD CONTAINER */}
      <div
        className="flashcard-container"
        style={{ perspective: '1000px', cursor: isMCQ ? 'default' : 'pointer', minHeight: '400px' }}
        onClick={() => !isMCQ && setIsFlipped(!isFlipped)}
      >
        {isMCQ ? (
          // --- MCQ LAYOUT ---
          <div style={{
            background: 'white', borderRadius: '20px', padding: '40px',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)', height: '100%',
            display: 'flex', flexDirection: 'column'
          }}>
            <div style={{ flex: 1 }}>
              {/* Tags */}
              {currentCard.tags && (
                <div style={{ display: 'flex', gap: '8px', marginBottom: '15px' }}>
                  {currentCard.tags.map((tag: string, i: number) => (
                    <span key={i} style={{ background: '#f1f5f9', color: '#64748b', padding: '4px 8px', borderRadius: '4px', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <FaTag size={10} /> {tag}
                    </span>
                  ))}
                </div>
              )}

              <h3 style={{ fontSize: '1.4rem', color: '#1e293b', marginBottom: '30px' }}>
                <ReactMarkdown>{sanitizeContent(currentCard.front)}</ReactMarkdown>
              </h3>

              {/* Options Grid */}
              <div style={{ display: 'grid', gap: '15px' }}>
                {currentCard.options?.map((opt: string, i: number) => {
                  let bg = "#f8fafc";
                  let border = "1px solid #e2e8f0";
                  const letter = String.fromCharCode(65 + i);

                  if (showResult) {
                    if (letter === currentCard.correctAnswer) {
                      bg = "#dcfce7"; border = "1px solid #22c55e"; // Green for Correct
                    } else if (opt === selectedOption) {
                      bg = "#fee2e2"; border = "1px solid #ef4444"; // Red for Wrong Selection
                    }
                  } else if (selectedOption === opt) {
                    bg = "#eff6ff"; border = "1px solid #3b82f6";
                  }

                  return (
                    <button
                      key={i}
                      onClick={(e) => { e.stopPropagation(); handleOptionClick(opt, i); }}
                      style={{
                        padding: '15px', borderRadius: '10px', textAlign: 'left',
                        background: bg, border: border, cursor: showResult ? 'default' : 'pointer',
                        fontSize: '1rem', color: '#334155', transition: 'all 0.2s',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                      }}
                    >
                      <div>
                        <span style={{ fontWeight: 600, marginRight: '10px' }}>{String.fromCharCode(65 + i)}.</span>
                        {opt}
                      </div>
                      {showResult && letter === currentCard.correctAnswer && <FaCheck color="#22c55e" />}
                      {showResult && opt === selectedOption && letter !== currentCard.correctAnswer && <FaTimes color="#ef4444" />}
                    </button>
                  );
                })}
              </div>
              {showResult && (
                <div style={{ marginTop: '20px', padding: '15px', background: '#f8fafc', borderRadius: '10px', borderLeft: '4px solid #8b5cf6' }}>
                  <p style={{ margin: 0, fontSize: '0.9rem', color: '#475569' }}>
                    <ReactMarkdown>{sanitizeContent(currentCard.back)}</ReactMarkdown>
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : (
          // --- STANDARD FLIP LAYOUT ---
          <div
            style={{
              position: 'relative', width: '100%', height: '100%', textAlign: 'center',
              transition: 'transform 0.6s', transformStyle: 'preserve-3d',
              transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
              minHeight: '400px'
            }}
          >
            {/* FRONT */}
            <div style={{
              position: 'absolute', width: '100%', height: '100%', backfaceVisibility: 'hidden',
              background: 'white', borderRadius: '20px', padding: '40px',
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
              display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center'
            }}>
              {/* NEW: Card Badge in Viewer */}
              <span className={`card-badge ${currentCard.type}`} style={{ top: '20px', left: '20px' }}>
                {currentCard.type}
              </span>

              {/* NEW: Tag Bar in Viewer for standard cards */}
              {currentCard.tags && (
                <div style={{ position: 'absolute', top: '24px', right: '40px', display: 'flex', gap: '8px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  {currentCard.tags.slice(0, 3).map((tag: string, i: number) => (
                    <span key={i} style={{ background: '#f1f5f9', color: '#64748b', padding: '4px 8px', borderRadius: '4px', fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <FaTag size={8} /> {tag}
                    </span>
                  ))}
                </div>
              )}

              <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#1e293b' }}>
                <ReactMarkdown>{sanitizeContent(currentCard.front)}</ReactMarkdown>
              </div>
              <p style={{ marginTop: '20px', color: '#94a3b8', fontSize: '0.9rem' }}>Click to flip</p>
            </div>

            {/* BACK */}
            <div style={{
              position: 'absolute', width: '100%', height: '100%', backfaceVisibility: 'hidden',
              background: '#f8fafc', borderRadius: '20px', padding: '40px',
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
              transform: 'rotateY(180deg)',
              display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center'
            }}>
              <div style={{ fontSize: '1.2rem', color: '#334155', lineHeight: '1.6' }}>
                <ReactMarkdown>{sanitizeContent(currentCard.back)}</ReactMarkdown>
              </div>

              {currentCard.sourceUrl && (
                <a
                  href={currentCard.sourceUrl}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  style={{ marginTop: '20px', color: '#3b82f6', display: 'flex', alignItems: 'center', gap: '5px' }}
                >
                  <FaExternalLinkAlt size={12} /> Source
                </a>
              )}
            </div>
          </div>
        )}
      </div>

      {/* NAVIGATION CONTROLS */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '30px', alignItems: 'center' }}>
        <button onClick={handlePrev} className="secondary-btn" disabled={currentIndex === 0}>
          Previous
        </button>

        {!isMCQ && (
          <div style={{ display: 'flex', gap: '20px' }}>
            <button
              onClick={() => handleRate(false)}
              style={{
                width: '50px', height: '50px', borderRadius: '50%', border: 'none',
                background: '#fee2e2', color: '#ef4444', fontSize: '1.2rem', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', transition: '0.2s'
              }}
            >
              <FaTimes />
            </button>
            <button
              onClick={() => handleRate(true)}
              style={{
                width: '50px', height: '50px', borderRadius: '50%', border: 'none',
                background: '#dcfce7', color: '#22c55e', fontSize: '1.2rem', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', transition: '0.2s'
              }}
            >
              <FaCheck />
            </button>
          </div>
        )}

        <button onClick={handleNext} className="generate-btn" style={{ margin: 0, background: '#8b5cf6' }}>
          {currentIndex === collection.flashcards.length - 1 ? 'Finish' : 'Next Card'}
        </button>
      </div>

    </div>
  );
};